import React from "react";
import { api } from "../lib/api";
import { toast } from "sonner";

/**
 * Admin: rotate the admin password from the UI.
 *
 * Backend contract: POST /api/auth/change-password
 *   body: { current_password, new_password }
 *   - validates current password against the live admin secret (bcrypt hash
 *     in MongoDB if rotated, ADMIN_PASSWORD env on first run)
 *   - enforces new_password ≥ 10 chars and contains a letter + a digit
 *   - returns { token, email } so we can hot-swap the locally stored token
 */
export default function AdminChangePassword() {
  const [form, setForm] = React.useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [show, setShow] = React.useState({ current: false, next: false });
  const [submitting, setSubmitting] = React.useState(false);

  const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const np = form.new_password;
  const strength = React.useMemo(() => {
    if (!np) return { score: 0, label: "—", color: "bg-zinc-200" };
    let score = 0;
    if (np.length >= 10) score += 1;
    if (np.length >= 14) score += 1;
    if (/[a-z]/.test(np) && /[A-Z]/.test(np)) score += 1;
    if (/\d/.test(np)) score += 1;
    if (/[^A-Za-z0-9]/.test(np)) score += 1;
    const labels = ["Too short", "Weak", "Fair", "Good", "Strong", "Excellent"];
    const colors = ["bg-red-500", "bg-red-400", "bg-amber-400", "bg-lime-500", "bg-emerald-500", "bg-emerald-600"];
    return { score, label: labels[score], color: colors[score] };
  }, [np]);

  const meetsMin = np.length >= 10 && /[A-Za-z]/.test(np) && /\d/.test(np);
  const matches = np !== "" && np === form.confirm;
  const canSubmit = form.current_password && meetsMin && matches && !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      // Hot-swap token so the existing session keeps working with the new password.
      if (data?.token) localStorage.setItem("admin_token", data.token);
      toast.success("Password updated. New password is now active.");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-change-password" className="max-w-xl">
      <div className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 mb-2">// SECURITY</div>
      <h1 className="font-display text-3xl tracking-tighter mb-2">CHANGE ADMIN PASSWORD</h1>
      <p className="font-mono-tech text-xs text-zinc-600 mb-8 leading-relaxed">
        Rotate the admin password. New password must be at least 10 characters and contain a letter and a digit.
        After saving, this device stays signed in with the new password; other devices will need to sign in again.
      </p>

      <form onSubmit={submit} className="space-y-6">
        <Field
          label="Current password"
          testid="cp-current"
          type={show.current ? "text" : "password"}
          value={form.current_password}
          onChange={set("current_password")}
          onToggle={() => setShow((s) => ({ ...s, current: !s.current }))}
          showToggleLabel={show.current ? "HIDE" : "SHOW"}
          autoComplete="current-password"
          required
        />

        <div>
          <Field
            label="New password"
            testid="cp-new"
            type={show.next ? "text" : "password"}
            value={form.new_password}
            onChange={set("new_password")}
            onToggle={() => setShow((s) => ({ ...s, next: !s.next }))}
            showToggleLabel={show.next ? "HIDE" : "SHOW"}
            autoComplete="new-password"
            required
          />
          <div className="mt-2">
            <div className="h-1 bg-zinc-200 overflow-hidden">
              <div
                data-testid="cp-strength-bar"
                className={`h-full transition-all ${strength.color}`}
                style={{ width: `${(strength.score / 5) * 100}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500">
              <span data-testid="cp-strength-label">Strength: {strength.label}</span>
              <span>{np.length}/128</span>
            </div>
          </div>
        </div>

        <Field
          label="Confirm new password"
          testid="cp-confirm"
          type={show.next ? "text" : "password"}
          value={form.confirm}
          onChange={set("confirm")}
          autoComplete="new-password"
          required
        />
        {form.confirm && !matches && (
          <p data-testid="cp-mismatch" className="font-mono-tech text-[10px] uppercase text-red-600">
            ⚠ Passwords do not match
          </p>
        )}

        <div className="pt-2 flex items-center gap-3">
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="cp-submit"
            className="font-mono-tech text-xs uppercase px-6 py-3 bg-zinc-950 text-white hover:bg-[#031833] disabled:opacity-40 disabled:cursor-not-allowed tracking-wider"
          >
            {submitting ? "Saving…" : "Update password →"}
          </button>
          <span className="font-mono-tech text-[10px] uppercase text-zinc-500">
            JWT will be rotated. Other devices logged out.
          </span>
        </div>
      </form>
    </div>
  );
}

function Field({ label, testid, type, value, onChange, onToggle, showToggleLabel, required, autoComplete }) {
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase tracking-wider text-zinc-500 block mb-2">
        {label}
      </label>
      <div className="relative">
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={onChange}
          required={required}
          autoComplete={autoComplete}
          className="w-full border border-zinc-300 px-3 py-2.5 font-mono-tech text-sm bg-white focus:border-[#031833] focus:outline-none"
        />
        {onToggle && (
          <button
            type="button"
            onClick={onToggle}
            data-testid={`${testid}-toggle`}
            className="absolute right-2 top-1/2 -translate-y-1/2 font-mono-tech text-[10px] uppercase text-zinc-500 hover:text-[#031833] tracking-wider"
          >
            {showToggleLabel}
          </button>
        )}
      </div>
    </div>
  );
}
